package com.appsyde.shared.presentation.screen.screen

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import kotlinx.coroutines.delay
import com.appsyde.shared.app.presentation.tickets.TicketsListViewModel
import com.appsyde.shared.presentation.screen.component.Spacer
import com.appsyde.shared.presentation.screen.component.SpacerSmall
import com.appsyde.shared.presentation.screen.component.ticket.TicketsSection
import com.appsyde.shared.presentation.screen.component.ticket.filterTicketsBySearch
import com.appsyde.shared.presentation.screen.component.ticket.sortTicketsFiveGroups
import com.appsyde.shared.presentation.screen.component.ticket.toTicketRowUi
import com.appsyde.shared.presentation.style.style.AppStylesCards
import com.appsyde.shared.presentation.style.style.AppStylesTickets
import com.appsyde.shared.presentation.style.style.InsightStyles
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.Style
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import kotlin.js.js

/** Onglet liste : "Ticket à voir" = mes tickets + libres ; "Tous" = + ceux pris par d'autres. */
private enum class TicketsViewTab { TicketAVoir, Tous }

/**
 * Écran liste tickets branché sur les données (TicketsListViewModel).
 * [currentUserAccountId] sert au tri 5 groupes et au variant visuel des lignes (mine / free / other).
 */
@Composable
fun TicketsScreenContent(viewModel: TicketsListViewModel, currentUserAccountId: String? = null) {
    // List is already loaded by loadFirstScreen() before this screen is shown (initialLoadDone gate in Main).
    var nowMs by remember { mutableStateOf(0L) }
    LaunchedEffect(Unit) { nowMs = (js("Date.now()") as Number).toLong() }
    val listState by viewModel.state.collectAsState()
    val selectedTicketId by viewModel.selectedTicketId.collectAsState()
    val searchInput by viewModel.searchQuery.collectAsState()
    var ticketsTab by remember { mutableStateOf(TicketsViewTab.TicketAVoir) }

    var debouncedQuery by remember { mutableStateOf("") }
    LaunchedEffect(searchInput) {
        delay(250)
        debouncedQuery = searchInput
    }

    val effectiveNow = if (nowMs > 0L) nowMs else (js("Date.now()") as Number).toLong()
    val extractByTicketId = listState.extractByTicketId
    val sortedOpen = sortTicketsFiveGroups(listState.openTickets, currentUserAccountId, effectiveNow)
    val sortedWaiting = sortTicketsFiveGroups(listState.waitingTickets, currentUserAccountId, effectiveNow)
    val sortedClosed = sortTicketsFiveGroups(listState.closedTickets, currentUserAccountId, effectiveNow)
    val openRows = sortedOpen.map { it.toTicketRowUi(effectiveNow, extractByTicketId[it.ticketId], currentUserAccountId) }
    val waitingRows = sortedWaiting.map { it.toTicketRowUi(effectiveNow, extractByTicketId[it.ticketId], currentUserAccountId) }
    val closedRows = sortedClosed.map { it.toTicketRowUi(effectiveNow, extractByTicketId[it.ticketId], currentUserAccountId) }
    val openRowsForTab = remember(openRows, ticketsTab) {
        if (ticketsTab == TicketsViewTab.TicketAVoir) openRows.filter { it.rowVariant != "other" } else openRows
    }
    val waitingRowsForTab = remember(waitingRows, ticketsTab) {
        if (ticketsTab == TicketsViewTab.TicketAVoir) waitingRows.filter { it.rowVariant != "other" } else waitingRows
    }
    val closedRowsForTab = remember(closedRows, ticketsTab) {
        if (ticketsTab == TicketsViewTab.TicketAVoir) closedRows.filter { it.rowVariant != "other" } else closedRows
    }
    val filteredOpen = remember(openRowsForTab, debouncedQuery) { filterTicketsBySearch(openRowsForTab, debouncedQuery) }
    val filteredWaiting = remember(waitingRowsForTab, debouncedQuery) { filterTicketsBySearch(waitingRowsForTab, debouncedQuery) }
    val filteredClosed = remember(closedRowsForTab, debouncedQuery) { filterTicketsBySearch(closedRowsForTab, debouncedQuery) }

    var expandedOpen by remember { mutableStateOf(true) }
    var expandedWaiting by remember { mutableStateOf(true) }
    var expandedClosed by remember { mutableStateOf(true) }

    LaunchedEffect(debouncedQuery) {
        if (debouncedQuery.isNotBlank()) {
            expandedOpen = true
            expandedWaiting = true
            expandedClosed = true
        }
    }

    Style(AppStylesCards)
    Style(InsightStyles)
    Style(AppStylesTickets)

    Div({ classes(AppStylesCards.pageCardGlass) }) {
        Div({ classes(AppStylesCards.ticketCardHeader) }) {
            Div({ classes(AppStylesTickets.headerLeft) }) {
                Div({ classes(AppStylesTickets.headerTitleCol) }) {
                    //Div({ classes(AppStylesTickets.headerKicker) }) { Text("Tickets") }
                    Div({ classes(AppStylesTickets.headerTitle) }) { Text("Boîte de réception") }
                }
                Div({ classes(AppStylesTickets.headerSub) }) {
                    Text("Vue d’ensemble")
                }
            }
            Div({ classes(AppStylesTickets.headerRight) }) {
                Button(attrs = {
                    classes(
                        AppStylesTickets.pillSectionHeader,
                        if (expandedOpen) AppStylesTickets.pillSectionHeaderActive else AppStylesTickets.pillSectionHeaderInactive
                    )
                    attr("type", "button")
                    onClick {
                        expandedOpen = !expandedOpen
                    }
                }) {
                    Text("À traiter (${filteredOpen.size})")
                }
                Button(attrs = {
                    classes(
                        AppStylesTickets.pillSectionHeader,
                        if (expandedWaiting) AppStylesTickets.pillSectionHeaderActive else AppStylesTickets.pillSectionHeaderInactive
                    )
                    attr("type", "button")
                    onClick {
                        expandedWaiting = !expandedWaiting
                    }
                }) {
                    Text("En attente (${filteredWaiting.size})")
                }
                Button(attrs = {
                    classes(
                        AppStylesTickets.pillSectionHeader,
                        if (expandedClosed) AppStylesTickets.pillSectionHeaderActive else AppStylesTickets.pillSectionHeaderInactive
                    )
                    attr("type", "button")
                    onClick {
                        expandedClosed = !expandedClosed
                    }
                }) {
                    Text("Fermé (${filteredClosed.size})")
                }
            }
        }
        Div({ classes(AppStylesCards.pageCardBody, AppStylesTickets.body) }) {
            Div({ classes(AppStylesTickets.toolbar) }) {
                Div({ classes(AppStylesTickets.ticketsViewTabsRow) }) {
                    Button(attrs = {
                        classes(*listOfNotNull(AppStylesTickets.ticketsViewTab, if (ticketsTab == TicketsViewTab.TicketAVoir) AppStylesTickets.ticketsViewTabActive else null).toTypedArray())
                        attr("type", "button")
                        onClick { ticketsTab = TicketsViewTab.TicketAVoir }
                    }) { Text("Tickets à voir") }
                    Button(attrs = {
                        classes(*listOfNotNull(AppStylesTickets.ticketsViewTab, if (ticketsTab == TicketsViewTab.Tous) AppStylesTickets.ticketsViewTabActive else null).toTypedArray())
                        attr("type", "button")
                        onClick { ticketsTab = TicketsViewTab.Tous }
                    }) { Text("Tous") }
                }
                Div({ classes(AppStylesTickets.searchWrap) }) {
                    Div({ classes(AppStylesTickets.searchIcon) }) { Text("⌘") }
                    Input(InputType.Text) {
                        classes(AppStylesTickets.searchInput)
                        value(searchInput)
                        onInput { viewModel.setSearchQuery((it.target as? org.w3c.dom.HTMLInputElement)?.value ?: "") }
                        attr("placeholder", "Sujet, email, intention…")
                    }
                    if (searchInput.isNotBlank()) {
                        Button({
                            classes(AppStylesTickets.searchHint)
                            onClick { viewModel.setSearchQuery("") }
                        }) { Text("✕") }
                    }
                }
               /* Div({ classes(AppStylesTickets.filtersWrap) }) {
                    Div({ classes(AppStylesTickets.filterGroup) }) {
                        Div({ classes(AppStylesTickets.filterLabel) }) { Text("État") }
                        Div({ classes(AppStylesTickets.pillsRow) }) {
                            Div({ classes(AppStylesTickets.pill, AppStylesTickets.pillActive) }) { Text("Tous") }
                            Div({ classes(AppStylesTickets.pill) }) { Text("Open") }
                            Div({ classes(AppStylesTickets.pill) }) { Text("Waiting") }
                            Div({ classes(AppStylesTickets.pill) }) { Text("Closed") }
                        }
                    }
                    Div({ classes(AppStylesTickets.filterGroup) }) {
                        Div({ classes(AppStylesTickets.filterLabel) }) { Text("Criticité") }
                        Div({ classes(AppStylesTickets.pillsRow) }) {
                            Div({ classes(AppStylesTickets.pill) }) { Text("Toutes") }
                            Div({ classes(AppStylesTickets.pill, AppStylesTickets.pillUrgent) }) { Text("Urgente") }
                            Div({ classes(AppStylesTickets.pill, AppStylesTickets.pillHigh) }) { Text("Haute") }
                        }
                    }
                    Div({ classes(AppStylesTickets.filterGroup) }) {
                        Div({ classes(AppStylesTickets.filterLabel) }) { Text("Tri") }
                        Div({ classes(AppStylesTickets.selectPill) }) {
                            Text("Dernier message")
                            Span({ classes(AppStylesTickets.chev) }) { Text("▾") }
                        }
                    }
                } */
            }
            TicketsSection(
                title = "À traiter",
                subtitle = "Nouveaux messages",
                rows = filteredOpen,
                selectedTicketId = selectedTicketId,
                onTicketSelected = { viewModel.selectTicket(it) },
                expanded = expandedOpen,
                onToggleExpand = { expandedOpen = !expandedOpen }
            )
            TicketsSection(
                title = "En attente",
                subtitle = "Réponse client attendue",
                rows = filteredWaiting,
                selectedTicketId = selectedTicketId,
                onTicketSelected = { viewModel.selectTicket(it) },
                expanded = expandedWaiting,
                onToggleExpand = { expandedWaiting = !expandedWaiting }
            )
            TicketsSection(
                title = "Terminés",
                subtitle = "Lecture seule",
                rows = filteredClosed,
                selectedTicketId = selectedTicketId,
                onTicketSelected = { viewModel.selectTicket(it) },
                expanded = expandedClosed,
                onToggleExpand = { expandedClosed = !expandedClosed }
            )
            SpacerSmall()
        }
    }
}

